// frontend/src/Pages/AdminUserManager.jsx
import React, { useState, useEffect } from "react";
import { apiClient } from "../apiClient";

const AdminUserManager = () => {
  const [users, setUsers] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [activeTab, setActiveTab] = useState('users');

  const [newUsername, setNewUsername] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newRole, setNewRole] = useState('user');

  const [empName, setEmpName] = useState('');
  const [empPosition, setEmpPosition] = useState('');
  const [empSalary, setEmpSalary] = useState('');

  const [partnerName, setPartnerName] = useState('');
  const [partnerDesignation, setPartnerDesignation] = useState('');

  const [resetUsername, setResetUsername] = useState('');
  const [resetLink, setResetLink] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [usersData, empData, partData] = await Promise.all([
          apiClient.get("/auth/users"),
          apiClient.get("/hr/employees"),
          apiClient.get("/hr/partners")
        ]);
        setUsers(usersData.filter(u => u.username !== "developer_admin"));
        setEmployees(empData);
        setPartners(partData);
      } catch (err) {
        setMessage("❌ Failed to load data");
      }
    };
    fetchData();
  }, []);

  const handleCreateUser = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');

    if (!newUsername || !newPassword) {
      setMessage('⚠️ Username and password required');
      setLoading(false);
      return;
    }

    try {
      const newUser = await apiClient.post("/auth/users", {
        username: newUsername,
        password: newPassword,
        role: newRole
      });
      setUsers(prev => [...prev, newUser].filter(u => u.username !== "developer_admin"));
      setMessage('✅ User created successfully!');
      setNewUsername('');
      setNewPassword('');
      setNewRole('user');
    } catch (err) {
      setMessage(`❌ ${err.message || "Failed to create user"}`);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateEmployee = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');

    if (!empName || !empPosition || !empSalary) {
      setMessage('⚠️ All employee fields required');
      setLoading(false);
      return;
    }

    try {
      const newEmp = await apiClient.post("/hr/employees", {
        full_name: empName,
        position: empPosition,
        monthly_salary_groszy: Math.round(parseFloat(empSalary) * 100)
      });
      setEmployees([...employees, newEmp]);
      setMessage('✅ Employee added successfully!');
      setEmpName('');
      setEmpPosition('');
      setEmpSalary('');
    } catch (err) {
      setMessage(`❌ ${err.message || "Failed to create employee"}`);
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePartner = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');

    if (!partnerName) {
      setMessage('⚠️ Partner name required');
      setLoading(false);
      return;
    }

    try {
      const newPartner = await apiClient.post("/hr/partners", {
        full_name: partnerName,
        designation: partnerDesignation || null
      });
      setPartners([...partners, newPartner]);
      setMessage('✅ Partner added successfully!');
      setPartnerName('');
      setPartnerDesignation('');
    } catch (err) {
      setMessage(`❌ ${err.message || "Failed to create partner"}`);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateRole = async (userId, newRole, isSuperuser) => {
    if (isSuperuser) {
      setMessage('⚠️ Cannot change superuser role');
      return;
    }
    try {
      const updatedUser = await apiClient.put(`/auth/users/${userId}/role`, { role: newRole });
      setUsers(users.map(u => u.id === userId ? updatedUser : u));
      setMessage('✅ Role updated successfully');
    } catch (err) {
      setMessage(`❌ ${err.message || "Failed to update role"}`);
    }
  };

  const handleDeleteUser = async (userId, username, isSuperuser) => {
    if (isSuperuser) {
      setMessage('⚠️ Cannot delete superuser');
      return;
    }
    if (!window.confirm(`Delete user "${username}"? This cannot be undone.`)) return;
    try {
      await apiClient.delete(`/auth/users/${userId}`);
      setUsers(users.filter(u => u.id !== userId));
      setMessage('✅ User deleted');
    } catch (err) {
      setMessage(`❌ ${err.message || "Failed to delete user"}`);
    }
  };

  const handleDeleteEmployee = async (empId, empName) => {
    if (!window.confirm(`Delete employee "${empName}"?`)) return;
    try {
      await apiClient.delete(`/hr/employees/${empId}`);
      setEmployees(employees.filter(e => e.id !== empId));
      setMessage('✅ Employee deleted');
    } catch (err) {
      setMessage(`❌ ${err.message || "Failed to delete employee"}`);
    }
  };

  const handleDeletePartner = async (partnerId, partnerName) => {
    if (!window.confirm(`Delete partner "${partnerName}"?`)) return;
    try {
      await apiClient.delete(`/hr/partners/${partnerId}`);
      setPartners(partners.filter(p => p.id !== partnerId));
      setMessage('✅ Partner deleted');
    } catch (err) {
      setMessage(`❌ ${err.message || "Failed to delete partner"}`);
    }
  };

  const handleGenerateResetLink = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');
    setResetLink('');
    try {
      const response = await apiClient.post("/auth/admin/reset-link", {
        username: resetUsername
      });
      setResetLink(response.reset_link);
      setMessage('✅ Reset link generated!');
    } catch (err) {
      setMessage(`❌ ${err.message || "Failed to generate reset link"}`);
    } finally {
      setLoading(false);
    }
  };

  const getRoleStyle = (role) => {
    const styles = {
      admin:            { bg: '#FEE2E2', color: '#991B1B', label: 'ADMIN' },
      manager:          { bg: '#DBEAFE', color: '#1E40AF', label: 'MANAGER' },
      driver:           { bg: '#D1FAE5', color: '#065F46', label: 'DRIVER' },
      partner:          { bg: '#EDE9FE', color: '#5B21B6', label: 'PARTNER' },
      production_staff: { bg: '#FEF9C3', color: '#854D0E', label: 'PROD' },
      owner:            { bg: '#FFE4E6', color: '#9F1239', label: 'OWNER' },
      staff:            { bg: '#F3F4F6', color: '#374151', label: 'STAFF' },
      user:             { bg: '#F3F4F6', color: '#374151', label: 'USER' },
    };
    return styles[role] || styles.user;
  };

  const tabs = [
    { id: 'users', label: '👤 System Users', count: users.length },
    { id: 'employees', label: '👷 Employees', count: employees.length },
    { id: 'partners', label: '🤝 Partners', count: partners.length },
  ];

  return (
    <>
      <style>{`
        .admin-user-manager {
          --bg-deep: #F5E9D8;
          --panel: #FFFFFF;
          --edge: rgba(45, 47, 94, 0.15);
          --accent-primary: #F76B4C;
          --accent-secondary: #2D2F5E;
          --accent-danger: #DC2626;
          --text-primary: #2D2F5E;
          --text-dim: #6B7280;
          font-family: 'Inter', system-ui, -apple-system, sans-serif;
          background: var(--bg-deep);
          color: var(--text-primary);
          min-height: 100vh;
          padding: 24px;
          max-width: 1100px;
          margin: 0 auto;
          box-sizing: border-box;
        }
        .admin-user-manager * { box-sizing: border-box; }
        .admin-user-manager .mono { font-family: 'JetBrains Mono', 'IBM Plex Mono', 'Fira Code', monospace; }
        .admin-user-manager .panel {
          background: var(--panel);
          border: 1px solid var(--edge);
          border-radius: 12px;
          padding: 20px;
          margin-bottom: 20px;
          box-shadow: 0 1px 4px rgba(45,47,94,0.08);
        }
        .admin-user-manager .input-field {
          background: white;
          border: 1px solid var(--edge);
          color: var(--text-primary);
          padding: 10px 12px;
          border-radius: 8px;
          font-size: 14px;
          outline: none;
          width: 100%;
          transition: border-color 0.2s, box-shadow 0.2s;
        }
        .admin-user-manager .input-field:focus {
          border-color: var(--accent-primary);
          box-shadow: 0 0 0 2px rgba(247, 107, 76, 0.2);
        }
        .admin-user-manager select.input-field {
          appearance: none;
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' fill='%236B7280' viewBox='0 0 16 16'%3E%3Cpath d='M7.247 11.14 2.451 5.658C1.885 5.013 2.345 4 3.204 4h9.592a1 1 0 0 1 .753 1.659l-4.796 5.48a1 1 0 0 1-1.506 0z'/%3E%3C/svg%3E");
          background-repeat: no-repeat;
          background-position: calc(100% - 12px) center;
          padding-right: 32px;
        }
        .admin-user-manager .btn-primary {
          background: var(--accent-primary);
          color: white;
          border: none;
          border-radius: 8px;
          padding: 10px 16px;
          font-weight: 600;
          cursor: pointer;
          font-size: 14px;
          transition: background 0.2s, box-shadow 0.2s;
        }
        .admin-user-manager .btn-primary:hover:not(:disabled) {
          background: #e55a3b;
          box-shadow: 0 4px 12px rgba(247, 107, 76, 0.3);
        }
        .admin-user-manager .btn-primary:disabled {
          background: #9CA3AF;
          cursor: not-allowed;
        }
        .admin-user-manager .btn-secondary {
          background: var(--accent-secondary);
          color: white;
          border: none;
          border-radius: 8px;
          padding: 10px 16px;
          font-weight: 600;
          cursor: pointer;
          font-size: 14px;
          transition: background 0.2s, box-shadow 0.2s;
        }
        .admin-user-manager .btn-secondary:hover:not(:disabled) {
          background: #1e2148;
          box-shadow: 0 4px 12px rgba(45, 47, 94, 0.3);
        }
        .admin-user-manager .btn-secondary:disabled {
          background: #9CA3AF;
          cursor: not-allowed;
        }
        .admin-user-manager .btn-danger {
          background: #FEE2E2;
          color: var(--accent-danger);
          border: none;
          border-radius: 6px;
          padding: 6px 12px;
          font-weight: 600;
          cursor: pointer;
          font-size: 12px;
          transition: background 0.2s;
        }
        .admin-user-manager .btn-danger:hover:not(:disabled) {
          background: #fecaca;
        }
        .admin-user-manager .btn-danger:disabled {
          background: #F3F4F6;
          color: #9CA3AF;
          cursor: not-allowed;
        }
        .admin-user-manager .tab-btn {
          background: white;
          color: var(--accent-secondary);
          border: 1px solid var(--edge);
          padding: 8px 18px;
          border-radius: 8px;
          cursor: pointer;
          font-weight: 600;
          font-size: 13px;
          transition: all 0.2s;
          box-shadow: 0 1px 3px rgba(0,0,0,0.08);
        }
        .admin-user-manager .tab-btn.active {
          background: var(--accent-secondary);
          color: white;
          border-color: var(--accent-secondary);
          box-shadow: 0 2px 8px rgba(45,47,94,0.25);
        }
        .admin-user-manager .role-badge {
          font-family: 'JetBrains Mono', 'IBM Plex Mono', monospace;
          font-size: 11px;
          font-weight: 700;
          padding: 4px 8px;
          border-left: 3px solid;
          border-radius: 4px;
          letter-spacing: 0.5px;
          display: inline-block;
        }
        .admin-user-manager .table-container {
          overflow-x: auto;
          -webkit-overflow-scrolling: touch;
          border-radius: 12px;
          border: 1px solid var(--edge);
          background: var(--panel);
          box-shadow: 0 1px 4px rgba(45,47,94,0.08);
        }
        .admin-user-manager .data-table {
          width: 100%;
          border-collapse: collapse;
          min-width: 600px;
        }
        .admin-user-manager .data-table th {
          background: var(--accent-secondary);
          color: white;
          padding: 12px 16px;
          text-align: left;
          font-size: 12px;
          font-weight: 600;
          letter-spacing: 0.05em;
          text-transform: uppercase;
        }
        .admin-user-manager .data-table td {
          padding: 12px 16px;
          border-bottom: 1px solid #F3F4F6;
          font-size: 14px;
          color: var(--text-primary);
        }
        .admin-user-manager .data-table tr:last-child td { border-bottom: none; }
        .admin-user-manager .data-table tr:hover td { background: #FAFAFA; }

        /* Mobile Cards */
        .mobile-view { display: none; }
        .desktop-view { display: block; }

        @media (max-width: 640px) {
          .mobile-view { display: block; }
          .desktop-view { display: none; }
          
          .mobile-card {
            background: var(--panel);
            border: 1px solid var(--edge);
            border-radius: 12px;
            padding: 16px;
            margin-bottom: 12px;
            box-shadow: 0 1px 4px rgba(45,47,94,0.08);
          }
          .mobile-card-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 12px;
            gap: 10px;
          }
          .mobile-username {
            font-weight: 600;
            font-size: 16px;
            color: var(--text-primary);
            word-break: break-word;
          }
          .mobile-divider {
            height: 1px;
            background: var(--edge);
            margin: 12px 0;
          }
          .mobile-label {
            display: block;
            font-size: 11px;
            color: var(--text-dim);
            text-transform: uppercase;
            letter-spacing: 0.05em;
            margin-bottom: 6px;
            font-family: 'JetBrains Mono', monospace;
          }
          .mobile-card .input-field,
          .mobile-card select.input-field { margin-bottom: 12px; }
          .mobile-card .btn-danger {
            width: 100%;
            padding: 10px;
            font-size: 14px;
            text-align: center;
          }
        }

        /* Message Box */
        .admin-user-manager .msg-box {
          padding: 12px 16px;
          border-radius: 8px;
          font-size: 14px;
          margin-bottom: 16px;
          display: flex;
          align-items: center;
          gap: 8px;
          border: 1px solid;
        }
        .admin-user-manager .msg-success {
          background: #D1FAE5;
          color: #065F46;
          border-color: #6EE7B7;
        }
        .admin-user-manager .msg-error {
          background: #FEE2E2;
          color: #991B1B;
          border-color: #FECACA;
        }
      `}</style>

      <div className="admin-user-manager">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '16px' }}>
          <div>
            <h1 style={{ fontSize: '22px', fontWeight: '700', color: 'var(--text-primary)', margin: 0 }}>
              👥 User & HR Management
            </h1>
            <p style={{ fontSize: '13px', color: 'var(--text-dim)', marginTop: '4px' }}>
              Manage system access, employees and partners
            </p>
          </div>
          <div style={{
            background: 'var(--accent-secondary)', color: 'white', borderRadius: '10px',
            padding: '8px 16px', fontSize: '13px', fontWeight: '600'
          }}>
            {users.length + employees.length + partners.length} Total Records
          </div>
        </div>

        {message && (
          <div className={`msg-box ${message.includes('✅') ? 'msg-success' : 'msg-error'}`}>
            {message}
          </div>
        )}

        <div className="panel">
          <h2 style={{ fontSize: '15px', fontWeight: '700', color: 'var(--text-primary)', marginBottom: '14px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ background: 'var(--accent-primary)', color: 'white', borderRadius: '6px', padding: '2px 8px', fontSize: '12px' }}>NEW</span>
            Add System User
          </h2>
          <form onSubmit={handleCreateUser}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '10px' }}>
              <input type="text" value={newUsername} onChange={(e) => setNewUsername(e.target.value)} placeholder="Username" className="input-field mono" required />
              <input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="Password" className="input-field mono" required />
              <select value={newRole} onChange={(e) => setNewRole(e.target.value)} className="input-field mono">
                <option value="user">User</option>
                <option value="admin">Admin</option>
                <option value="manager">Manager</option>
                <option value="driver">Driver</option>
                <option value="partner">Partner</option>
                <option value="production_staff">Production Staff</option>
              </select>
              <button type="submit" disabled={loading} className="btn-primary">
                {loading ? 'Creating...' : '+ Create User'}
              </button>
            </div>
          </form>
        </div>

        <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap' }}>
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`tab-btn ${activeTab === tab.id ? 'active' : ''}`}
            >
              {tab.label}
              <span style={{
                marginLeft: '6px',
                background: activeTab === tab.id ? 'rgba(255,255,255,0.25)' : 'var(--bg-deep)',
                color: activeTab === tab.id ? 'white' : 'var(--accent-secondary)',
                borderRadius: '10px', padding: '1px 7px', fontSize: '11px'
              }}>
                {tab.count}
              </span>
            </button>
          ))}
        </div>

        {activeTab === 'users' && (
          <>
            {/* Desktop View */}
            <div className="desktop-view">
              <div className="table-container">
                <table className="data-table">
                  <thead>
                    <tr>
                      {['#', 'Username', 'Role', 'Change Role', 'Actions'].map(h => (
                        <th key={h}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {users.length === 0 ? (
                      <tr><td colSpan={5} style={{ textAlign: 'center', color: 'var(--text-dim)', padding: '32px' }}>No users found</td></tr>
                    ) : (
                      users.map((user, idx) => {
                        const roleStyle = getRoleStyle(user.role);
                        const isSuperuser = user.is_superuser === true;
                        return (
                          <tr key={user.id}>
                            <td className="mono" style={{ color: 'var(--text-dim)', fontSize: '13px' }}>{idx + 1}</td>
                            <td>
                              <span style={{ fontWeight: '600' }}>{user.username}</span>
                              {isSuperuser && (
                                <span className="mono" style={{ marginLeft: '6px', fontSize: '10px', background: '#FEE2E2', color: '#991B1B', borderRadius: '4px', padding: '1px 5px', fontWeight: '700' }}>
                                  PROTECTED
                                </span>
                              )}
                            </td>
                            <td>
                              <span className="role-badge" style={{ borderColor: roleStyle.color, background: roleStyle.bg, color: roleStyle.color }}>
                                [{roleStyle.label}]
                              </span>
                            </td>
                            <td>
                              <select
                                value={user.role}
                                onChange={(e) => handleUpdateRole(user.id, e.target.value, isSuperuser)}
                                disabled={isSuperuser}
                                className="input-field mono"
                                style={{ width: 'auto', minWidth: '140px', padding: '6px 10px', fontSize: '12px' }}
                              >
                                <option value="user">User</option>
                                <option value="admin">Admin</option>
                                <option value="manager">Manager</option>
                                <option value="driver">Driver</option>
                                <option value="partner">Partner</option>
                                <option value="production_staff">Production Staff</option>
                              </select>
                            </td>
                            <td>
                              <button
                                onClick={() => handleDeleteUser(user.id, user.username, isSuperuser)}
                                disabled={isSuperuser}
                                className="btn-danger"
                              >
                                🗑 Delete
                              </button>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Mobile View */}
            <div className="mobile-view">
              {users.length === 0 ? (
                <div style={{ textAlign: 'center', color: 'var(--text-dim)', padding: '32px' }}>No users found</div>
              ) : (
                users.map((user) => {
                  const roleStyle = getRoleStyle(user.role);
                  const isSuperuser = user.is_superuser === true;
                  return (
                    <div key={user.id} className="mobile-card">
                      <div className="mobile-card-header">
                        <span className="mobile-username">
                          {user.username}
                          {isSuperuser && <span className="mono" style={{ marginLeft: '8px', fontSize: '10px', color: '#991B1B', background: '#FEE2E2', padding: '2px 6px', borderRadius: '4px' }}>PROTECTED</span>}
                        </span>
                        <span className="role-badge" style={{ borderColor: roleStyle.color, background: roleStyle.bg, color: roleStyle.color }}>
                          [{roleStyle.label}]
                        </span>
                      </div>
                      <div className="mobile-divider"></div>
                      <div className="mobile-card-body">
                        <label className="mobile-label">Change Role:</label>
                        <select
                          value={user.role}
                          onChange={(e) => handleUpdateRole(user.id, e.target.value, isSuperuser)}
                          disabled={isSuperuser}
                          className="input-field mono"
                        >
                          <option value="user">User</option>
                          <option value="admin">Admin</option>
                          <option value="manager">Manager</option>
                          <option value="driver">Driver</option>
                          <option value="partner">Partner</option>
                          <option value="production_staff">Production Staff</option>
                        </select>
                        <button
                          onClick={() => handleDeleteUser(user.id, user.username, isSuperuser)}
                          disabled={isSuperuser}
                          className="btn-danger"
                          style={{ width: '100%', marginTop: '8px' }}
                        >
                          🗑 Delete
                        </button>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </>
        )}

        {activeTab === 'employees' && (
          <>
            <div className="panel">
              <h2 style={{ fontSize: '15px', fontWeight: '700', color: 'var(--text-primary)', marginBottom: '14px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ background: 'var(--accent-secondary)', color: 'white', borderRadius: '6px', padding: '2px 8px', fontSize: '12px' }}>NEW</span>
                Add Employee
              </h2>
              <form onSubmit={handleCreateEmployee}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '10px' }}>
                  <input type="text" value={empName} onChange={(e) => setEmpName(e.target.value)} placeholder="Full Name" className="input-field mono" required />
                  <input type="text" value={empPosition} onChange={(e) => setEmpPosition(e.target.value)} placeholder="Position" className="input-field mono" required />
                  <input type="number" step="0.01" value={empSalary} onChange={(e) => setEmpSalary(e.target.value)} placeholder="Monthly Salary (PLN)" className="input-field mono" required />
                  <button type="submit" disabled={loading} className="btn-secondary">
                    {loading ? 'Saving...' : '+ Add Employee'}
                  </button>
                </div>
              </form>
            </div>

            {/* Desktop View */}
            <div className="desktop-view">
              <div className="table-container">
                <table className="data-table">
                  <thead>
                    <tr>
                      {['#', 'Full Name', 'Position', 'Monthly Salary', 'Actions'].map(h => (
                        <th key={h}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {employees.length === 0 ? (
                      <tr><td colSpan={5} style={{ textAlign: 'center', color: 'var(--text-dim)', padding: '32px' }}>No employees found</td></tr>
                    ) : (
                      employees.map((emp, idx) => (
                        <tr key={emp.id}>
                          <td className="mono" style={{ color: 'var(--text-dim)', fontSize: '13px' }}>{idx + 1}</td>
                          <td style={{ fontWeight: '600' }}>{emp.full_name}</td>
                          <td style={{ color: 'var(--text-dim)' }}>{emp.position}</td>
                          <td className="mono" style={{ fontWeight: '700', color: 'var(--text-primary)' }}>
                            {(emp.monthly_salary_groszy / 100).toFixed(2)} PLN
                          </td>
                          <td>
                            <button onClick={() => handleDeleteEmployee(emp.id, emp.full_name)} className="btn-danger">
                              🗑 Delete
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Mobile View */}
            <div className="mobile-view">
              {employees.length === 0 ? (
                <div style={{ textAlign: 'center', color: 'var(--text-dim)', padding: '32px' }}>No employees found</div>
              ) : (
                employees.map((emp) => (
                  <div key={emp.id} className="mobile-card">
                    <div className="mobile-card-header">
                      <span className="mobile-username">{emp.full_name}</span>
                      <span className="mono" style={{ color: 'var(--text-dim)', fontSize: '12px' }}>{emp.position}</span>
                    </div>
                    <div className="mobile-divider"></div>
                    <div className="mobile-card-body">
                      <label className="mobile-label">Monthly Salary:</label>
                      <div className="mono" style={{ fontSize: '18px', fontWeight: '700', color: 'var(--text-primary)', marginBottom: '12px' }}>
                        {(emp.monthly_salary_groszy / 100).toFixed(2)} PLN
                      </div>
                      <button onClick={() => handleDeleteEmployee(emp.id, emp.full_name)} className="btn-danger" style={{ width: '100%' }}>
                        🗑 Delete
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </>
        )}

        {activeTab === 'partners' && (
          <>
            <div className="panel">
              <h2 style={{ fontSize: '15px', fontWeight: '700', color: 'var(--text-primary)', marginBottom: '14px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ background: 'var(--accent-secondary)', color: 'white', borderRadius: '6px', padding: '2px 8px', fontSize: '12px' }}>NEW</span>
                Add Partner
              </h2>
              <form onSubmit={handleCreatePartner}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '10px' }}>
                  <input type="text" value={partnerName} onChange={(e) => setPartnerName(e.target.value)} placeholder="Full Name" className="input-field mono" required />
                  <input type="text" value={partnerDesignation} onChange={(e) => setPartnerDesignation(e.target.value)} placeholder="Designation (Optional)" className="input-field mono" />
                  <button type="submit" disabled={loading} className="btn-secondary">
                    {loading ? 'Saving...' : '+ Add Partner'}
                  </button>
                </div>
              </form>
            </div>

            {/* Desktop View */}
            <div className="desktop-view">
              <div className="table-container">
                <table className="data-table">
                  <thead>
                    <tr>
                      {['#', 'Full Name', 'Designation', 'Status', 'Actions'].map(h => (
                        <th key={h}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {partners.length === 0 ? (
                      <tr><td colSpan={5} style={{ textAlign: 'center', color: 'var(--text-dim)', padding: '32px' }}>No partners found</td></tr>
                    ) : (
                      partners.map((partner, idx) => (
                        <tr key={partner.id}>
                          <td className="mono" style={{ color: 'var(--text-dim)', fontSize: '13px' }}>{idx + 1}</td>
                          <td style={{ fontWeight: '600' }}>{partner.full_name}</td>
                          <td style={{ color: 'var(--text-dim)' }}>{partner.designation || '—'}</td>
                          <td>
                            <span style={{
                              background: '#D1FAE5', color: '#065F46', borderRadius: '20px',
                              padding: '3px 10px', fontSize: '12px', fontWeight: '600',
                              display: 'inline-flex', alignItems: 'center', gap: '4px'
                            }}>
                              <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#10B981', display: 'inline-block' }}></span>
                              Active
                            </span>
                          </td>
                          <td>
                            <button onClick={() => handleDeletePartner(partner.id, partner.full_name)} className="btn-danger">
                              🗑 Delete
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Mobile View */}
            <div className="mobile-view">
              {partners.length === 0 ? (
                <div style={{ textAlign: 'center', color: 'var(--text-dim)', padding: '32px' }}>No partners found</div>
              ) : (
                partners.map((partner) => (
                  <div key={partner.id} className="mobile-card">
                    <div className="mobile-card-header">
                      <span className="mobile-username">{partner.full_name}</span>
                      <span style={{
                        background: '#D1FAE5', color: '#065F46', borderRadius: '20px',
                        padding: '3px 10px', fontSize: '12px', fontWeight: '600',
                        display: 'inline-flex', alignItems: 'center', gap: '4px'
                      }}>
                        <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#10B981', display: 'inline-block' }}></span>
                        Active
                      </span>
                    </div>
                    <div className="mobile-divider"></div>
                    <div className="mobile-card-body">
                      <label className="mobile-label">Designation:</label>
                      <div style={{ color: 'var(--text-primary)', marginBottom: '12px', fontSize: '14px' }}>
                        {partner.designation || '—'}
                      </div>
                      <button onClick={() => handleDeletePartner(partner.id, partner.full_name)} className="btn-danger" style={{ width: '100%' }}>
                        🗑 Delete
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </>
        )}

        <div className="panel" style={{ marginTop: '24px' }}>
          <h2 style={{ fontSize: '15px', fontWeight: '700', color: 'var(--text-primary)', marginBottom: '6px' }}>
            🔑 Password Reset
          </h2>
          <p style={{ fontSize: '13px', color: 'var(--text-dim)', marginBottom: '14px' }}>
            Generate a reset link and share it with the user via WhatsApp or SMS.
          </p>
          <form onSubmit={handleGenerateResetLink}>
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
              <input
                type="text"
                value={resetUsername}
                onChange={(e) => setResetUsername(e.target.value)}
                placeholder="Enter username..."
                className="input-field mono"
                style={{ flex: 1, minWidth: '200px' }}
                required
              />
              <button type="submit" disabled={loading} className="btn-primary">
                {loading ? 'Generating...' : 'Generate Link'}
              </button>
            </div>
          </form>
          {resetLink && (
            <div style={{ marginTop: '14px', padding: '12px', background: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: '8px' }}>
              <p style={{ fontSize: '12px', color: '#1E40AF', marginBottom: '6px', fontWeight: '600' }}>Reset Link (valid 1 hour):</p>
              <a href={resetLink} target="_blank" rel="noopener noreferrer"
                className="mono"
                style={{ color: '#2563EB', fontSize: '13px', wordBreak: 'break-all' }}>
                {resetLink}
              </a>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default AdminUserManager;